/**
 * The deterministic simulation core (docs 02/03/04/12/14).
 *
 * Fixed 30 Hz timestep. Plain-data state, one seeded RNG, no rendering imports,
 * no wall-clock, no Math.sin/cos/atan2 (implementation-approximated — the
 * octant maths below uses only comparisons, sqrt and arithmetic, all of which
 * are IEEE-exact or correctly rounded). Same seed + same command list ⇒ the
 * same `hash()` at every tick, on every machine. That property is load-bearing:
 * replays, saves, the balance harness, and doc 13 §G's "determinism as
 * identity" all stand on it, and `tests/sim.test.ts` enforces it.
 */

import abilitiesJson from '../content/abilities.json' with { type: 'json' };
import buildings from '../content/buildings.json' with { type: 'json' };
import {
  CAPACITOR_MAX,
  CAPACITOR_RATE,
  CORE_POWER,
  DEPOSIT_STOCK,
  GENERATOR_BURN,
  GENERATOR_POWER,
  GRID_RADIUS,
  NEUTRAL_MODIFIERS,
  PLACEABLE,
  RES_IDS,
  STORAGE_BASE,
  STORAGE_PER_DEPOT,
  tierCaps,
  TIER_COSTS,
  towerStats,
  type EconStatus,
  type TowerStats,
} from './defs.ts';
import { REGEN_BREAK_DAMAGE, REGEN_BREAK_SECONDS, SPECIES } from './species.ts';
import { resolveProjectiles, runRevealPass, targetable, updateTowers } from './combat.ts';
import { updateFauna } from './fauna.ts';
import { recomputeConnectivity, refreshEconomyStatus, settleShift, shiftRoster, structCentre } from './economy.ts';
import { castAbility } from './abilities.ts';
import { facingOctant } from './facing.ts';
import {
  DOUBLE_REEL_BONUS,
  DT,
  GROUP_SPACING_S,
  MIN_DAMAGE,
  ORBIT_SECONDS,
  PANIC_TAX,
  PATIENCE_PIPS,
  RILED_HP_MULT,
  RUIN_REBUILD_FACTOR,
  RUIN_WAVES,
  SALVAGE_BASE,
  SALVAGE_PER_N,
  SELL_REFUND_BUILD,
  SELL_REFUND_COMBAT,
  TICK_RATE,
} from './const.ts';
import { computeFlowField, type FlowField } from './flowfield.ts';
import { hashState } from './hash.ts';
import { SimGrid, T_ROCK } from './grid.ts';
import { Rng } from './rng.ts';
import type {
  AbilityId,
  Command,
  EdgeId,
  Enemy,
  Phase,
  Projectile,
  Resources,
  RunModifiers,
  Scenario,
  SimEvent,
  SimState,
  Structure,
} from './types.ts';

// The tables live in defs.ts; the class re-exports what callers already use.
export { placeableDef, TIER_COSTS, towerStats, type PlaceableDef, type TowerStats, type EconStatus } from './defs.ts';
export { facingOctant } from './facing.ts';

// ---------------------------------------------------------------------------
// the sim
// ---------------------------------------------------------------------------

/** Perimeter side centres for orbit→edge distance, orbit ∈ [0,1): n e s w. */
const EDGE_ORDER: EdgeId[] = ['n', 'e', 's', 'w'];

export class Sim {
  state: SimState;
  readonly scenario: Scenario;
  /** The meta's numbers for this run (doc 08); neutral when nobody supplied any. */
  readonly mods: RunModifiers;
  grid: SimGrid;
  flow: FlowField;
  private flowDirty = true;
  private powerDirty = true;
  private events: SimEvent[] = [];
  /** The one seeded RNG — combat and spawning draw from it via the host. */
  readonly rng: Rng;

  // — derived per tick, read by the UI, never hashed or saved —
  /** Structure ids that can draw from the power grid (doc 04 connectivity). */
  connected = new Set<number>();
  /** Last tick's power solve. */
  power = { supply: CORE_POWER, demand: 0, efficiency: 1 };
  /** What each economy building is doing (working / stalled / offgrid). */
  status = new Map<number, EconStatus>();
  /** Storage cap per resource this tick (doc 04: 100 + 100 per connected Depot). */
  storageCap = STORAGE_BASE;

  constructor(scenario: Scenario, seed: number) {
    this.scenario = scenario;
    this.mods = scenario.modifiers ?? NEUTRAL_MODIFIERS;
    this.grid = new SimGrid(scenario.map);
    this.rng = new Rng(seed);

    const coreHp = Math.round(buildings.buildings.core_dome.hp * this.mods.coreHpMult);
    const core: Structure = {
      id: 1,
      def: 'core_dome',
      kind: 'core',
      i: scenario.map.core.i,
      j: scenario.map.core.j,
      size: buildings.buildings.core_dome.size,
      hp: coreHp,
      maxHp: coreHp,
      ruin: false,
      ruinWavesLeft: 0,
      cooldown: 0,
      targetId: -1,
      facing: 1,
      firedAt: -1,
      depositTiles: [],
      upgrades: { a: 0, b: 0, c: 0 },
      ramp: 0,
      healRate: 0,
      healUntil: -1,
      disabledUntil: -1,
    };

    const deposits: Record<string, number> = {};
    for (const [i, j] of scenario.map.oreVeins) {
      deposits[String(this.grid.idx(i!, j!))] = DEPOSIT_STOCK.vein;
    }
    for (const [i, j] of scenario.map.gasVents) {
      deposits[String(this.grid.idx(i!, j!))] = DEPOSIT_STOCK.vent;
    }

    this.state = {
      tick: 0,
      seed,
      rngState: this.rng.state(),
      outcome: 'playing',
      resources: { ...scenario.startingKit },
      coreId: core.id,
      nextId: 2,
      structures: [core],
      enemies: [],
      rim: scenario.shoals.map((s, k) => this.makeRimShoal(s, k / scenario.shoals.length)),
      waves: [],
      pendingSpawns: [],
      fightsCleared: 0,
      pullsMade: 0,
      deposits,
      capacitor: 0,
      projectiles: [],
      act: 1,
      lastPullTick: -1,
      abilityReady: { q: 0, w: 0, e: 0, r: 0 },
      stats: { damageByDef: {}, waveDamageByDef: {}, waveKillsByDef: {}, killedSpecies: [] },
    };
    this.grid.sync(this.state.structures);
    this.flow = this.recomputeFlow();
  }

  private makeRimShoal(s: Scenario['shoals'][number], orbit: number): SimState['rim'][number] {
    const out: SimState['rim'][number] = {
      id: s.id,
      name: s.name,
      menace: s.menace,
      composition: s.composition,
      orbit,
      patience: PATIENCE_PIPS,
      riled: false,
      state: 'circling',
    };
    if (s.boss) out.boss = s.boss;
    if (s.gimmick) out.gimmick = s.gimmick;
    return out;
  }

  // -- public surface -------------------------------------------------------

  get phase(): Phase {
    return this.state.waves.some((w) => !w.cleared) ? 'combat' : 'build';
  }

  /** Combat/economy systems report through here (CombatHost). */
  emit(ev: SimEvent): void {
    this.events.push(ev);
  }

  /** Render-layer feedback since the last drain. Derived — never hashed or saved. */
  drainEvents(): SimEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  /** Apply a player command. Returns null on success, or the rejection reason. */
  command(cmd: Command): string | null {
    if (this.state.outcome !== 'playing') return this.reject('THE RUN IS OVER');
    let err: string | null;
    switch (cmd.type) {
      case 'place':
        err = this.doPlace(cmd.def, cmd.i, cmd.j);
        break;
      case 'sell':
        err = this.doSell(cmd.structureId);
        break;
      case 'pull':
        err = this.doPull(cmd.shoalId);
        break;
      case 'upgrade':
        err = this.doUpgrade(cmd.structureId, cmd.path);
        break;
      case 'cast':
        err = this.doCast(cmd.ability, cmd.x, cmd.y);
        break;
    }
    this.state.rngState = this.rng.state();
    return err;
  }

  tick(): void {
    if (this.state.outcome !== 'playing') return;
    const s = this.state;
    s.tick++;

    // rim orbits advance while circling — the ocean is always alive (doc 14)
    for (const shoal of s.rim) {
      if (shoal.state === 'circling') {
        shoal.orbit = (shoal.orbit + DT / ORBIT_SECONDS) % 1;
      }
    }

    this.autoLure();
    this.spawnPending();
    if (this.flowDirty) this.flow = this.recomputeFlow();
    updateFauna(this);
    if (this.flowDirty) this.flow = this.recomputeFlow(); // a chew may have opened a path
    this.runEconomySystems(); // before towers: this tick's bullets are usable this tick
    runRevealPass(this); // who can be SEEN this tick, before anyone aims (doc 03)
    updateTowers(this);
    resolveProjectiles(this);
    this.reapEnemies();
    this.mendStructures();
    this.checkWaveClears();

    s.rngState = this.rng.state();
  }

  /** Two-lane FNV-1a over the full state, as 16 hex chars (src/sim/hash.ts). */
  hash(): string {
    return hashState(this.state);
  }

  /** Full state as JSON — floats round-trip exactly. */
  snapshot(): string {
    this.state.rngState = this.rng.state();
    return JSON.stringify(this.state);
  }

  static restore(scenario: Scenario, json: string): Sim {
    const state = JSON.parse(json) as SimState;
    const sim = new Sim(scenario, state.seed);
    // a save written before the meta bookkeeping existed still loads (doc 18 §4)
    state.stats ??= { damageByDef: {}, waveDamageByDef: {}, waveKillsByDef: {}, killedSpecies: [] };
    sim.state = state;
    sim.rng.restore(state.rngState);
    sim.grid.sync(state.structures);
    sim.flow = sim.recomputeFlow();
    return sim;
  }

  byId(id: number): Structure | undefined {
    return this.state.structures.find((x) => x.id === id);
  }

  /**
   * Meta bookkeeping (doc 08): damage credited to the def that caused it,
   * clamped to what the fish had left, so overkill never inflates XP. Combat
   * calls this through CombatHost; the sim's own DoT pass goes via `hurt`.
   */
  credit(def: string, dealt: number): void {
    if (def === '' || dealt <= 0) return;
    const st = this.state.stats;
    st.damageByDef[def] = (st.damageByDef[def] ?? 0) + dealt;
    st.waveDamageByDef[def] = (st.waveDamageByDef[def] ?? 0) + dealt;
  }

  /**
   * Grid health for a powered tower (doc 04/05): 0 when it is off the grid,
   * otherwise this tick's brownout efficiency. Beams and fans run on watts.
   */
  powerEff(structureId: number): number {
    if (!this.connected.has(structureId)) return 0;
    return this.power.efficiency;
  }

  /** Apply damage from a named source and credit exactly what landed. */
  hurt(e: Enemy, raw: number, def: string): number {
    if (raw <= 0) return 0;
    const dealt = raw * this.guardMult(e);
    this.credit(def, Math.min(dealt, Math.max(0, e.hp)));
    e.hp -= dealt;
    // Doc 06 Regen: burst, not trickle. One big hit stops the healing for 2 s;
    // thirty small ones a second do not, which is the whole counter-play.
    if (dealt >= REGEN_BREAK_DAMAGE) {
      e.regenPauseUntil = Math.max(e.regenPauseUntil, this.state.tick + REGEN_BREAK_SECONDS * TICK_RATE);
    }
    if (e.hp <= 0 && def !== '') e.lastHitBy = def;
    return dealt;
  }

  /**
   * Doc 06 (The Pale Lure): while enough of its own escort is still swimming
   * near it, the boss takes half damage. Clearing the swarm is the exam — the
   * boss bar simply refuses to move until you do.
   */
  private guardMult(e: Enemy): number {
    const g = SPECIES[e.species]!.guarded;
    if (!g) return 1;
    let alive = 0;
    const r2 = g.radius * g.radius;
    for (const other of this.state.enemies) {
      if (other.hp <= 0 || other.species !== g.species) continue;
      if ((other.x - e.x) * (other.x - e.x) + (other.y - e.y) * (other.y - e.y) > r2) continue;
      if (++alive >= g.minAlive) return g.damageMult;
    }
    return 1;
  }

  /** Doc 03/06: can a tower even see this thing right now? The HUD asks too. */
  visible(e: Enemy): boolean {
    return targetable(this.state.tick, e);
  }

  enemyById(id: number): Enemy | undefined {
    return this.state.enemies.find((x) => x.id === id);
  }

  // -- commands -------------------------------------------------------------

  private reject(reason: string): string {
    this.events.push({ type: 'rejected', reason });
    return reason;
  }

  private doPlace(def: string, i: number, j: number): string | null {
    const d = PLACEABLE[def];
    if (!d) return this.reject('NOT PLACEABLE YET');
    if (this.mods.allowedDefs && !this.mods.allowedDefs.includes(def)) {
      return this.reject('LOCKED — BUY IT IN COLONY COMMAND');
    }
    const check = this.grid.placeCheck(i, j, d.size, this.state.structures, d.wants ?? null);
    if (!check.ok) return this.reject(check.reason);

    // cost: 50% on a matching ruin (doc 03), +25% Panic Tax in combat (doc 02)
    let factor = check.ruinId !== -1 ? RUIN_REBUILD_FACTOR : 1;
    if (this.phase === 'combat') factor *= 1 + PANIC_TAX;
    factor *= this.mods.costMult; // INFLATION (doc 08 Pressure)
    // wages settle in fractions, so stocks carry float dust — an epsilon keeps
    // "exactly affordable" purchases from bouncing off 59.999999
    for (const res of RES_IDS) {
      const amt = Math.ceil((d.cost[res] ?? 0) * factor);
      if (this.state.resources[res] < amt - 1e-6) {
        return this.reject(`NOT ENOUGH ${res.toUpperCase()}`);
      }
    }
    for (const res of RES_IDS) {
      const amt = Math.ceil((d.cost[res] ?? 0) * factor);
      this.state.resources[res] = Math.max(0, this.state.resources[res] - amt);
    }

    if (check.ruinId !== -1) {
      this.state.structures = this.state.structures.filter((x) => x.id !== check.ruinId);
    }
    const hp = Math.round(d.hp * this.mods.structureHpMult);
    this.state.structures.push({
      id: this.state.nextId++,
      def,
      kind: d.kind,
      i,
      j,
      size: d.size,
      hp,
      maxHp: hp,
      ruin: false,
      ruinWavesLeft: 0,
      cooldown: 0,
      targetId: -1,
      facing: 1,
      firedAt: -1,
      depositTiles: check.depositTiles,
      upgrades: { a: 0, b: 0, c: 0 },
      ramp: 0,
      healRate: 0,
      healUntil: -1,
      disabledUntil: -1,
    });
    this.grid.sync(this.state.structures);
    this.flowDirty = true;
    this.powerDirty = true;
    const placed = this.state.structures[this.state.structures.length - 1]!;
    this.events.push({ type: 'placed', structureId: placed.id, def, i, j, size: d.size });
    return null;
  }

  private doSell(id: number): string | null {
    const s = this.byId(id);
    if (!s) return this.reject('NOTHING THERE');
    if (s.kind === 'core') return this.reject('THE LANDLORD STAYS');
    if (s.ruin) return this.reject('IT IS ALREADY RUBBLE');
    const d = PLACEABLE[s.def];
    if (!d) return this.reject('NOT SELLABLE');
    // Doc 04: a depleted extractor recycles for a FREE full refund, any phase
    // ("thank you for your service"). Everything else: 100% build / 50% combat —
    // on the TOTAL invested, upgrades included (doc 03).
    const depleted =
      s.depositTiles.length > 0 &&
      s.depositTiles.every((t) => (this.state.deposits[String(t)] ?? 0) <= 0);
    const refund =
      (depleted ? 1 : this.phase === 'build' ? SELL_REFUND_BUILD : SELL_REFUND_COMBAT) *
      this.mods.sellRefundMult; // NO REFUNDS (doc 08 Pressure)
    let upgradeSpend = 0;
    for (const p of ['a', 'b', 'c'] as const) {
      for (let t = 0; t < s.upgrades[p]; t++) upgradeSpend += TIER_COSTS[t]!;
    }
    for (const res of RES_IDS) {
      const invested = (d.cost[res] ?? 0) + (res === 'alloy' ? upgradeSpend : 0);
      this.addResource(res, Math.floor(invested * refund));
    }
    this.state.structures = this.state.structures.filter((x) => x.id !== id);
    this.grid.sync(this.state.structures);
    this.flowDirty = true;
    this.powerDirty = true;
    return null;
  }

  /** Doc 05: 3×3 per instance, pick-2-cap-1, T1/T2/T3 = 20/35/60 alloy. */
  private doUpgrade(id: number, path: 'a' | 'b' | 'c'): string | null {
    const st = this.byId(id);
    if (!st) return this.reject('NOTHING THERE');
    if (st.kind !== 'tower' || st.ruin) return this.reject('NOT UPGRADEABLE');
    const tier = st.upgrades[path];
    if (tier >= 3) return this.reject('PATH IS CAPPED');
    // doc 05 soft gate: the tower's own XP level decides how deep a path goes
    const levels = this.mods.towerLevels;
    if (levels && tier + 1 > tierCaps(levels[st.def] ?? 1)[path]) {
      return this.reject('LOCKED — THIS TOWER NEEDS MORE EXPERIENCE');
    }
    const others = (['a', 'b', 'c'] as const).filter((p) => p !== path);
    const invested = others.filter((p) => st.upgrades[p] > 0).length;
    if (tier === 0 && invested >= 2) return this.reject('PICK 2 — A TOWER LEARNS TWO PATHS, MAX');
    if (tier === 2 && others.some((p) => st.upgrades[p] >= 3)) {
      return this.reject('CAP 1 — ONLY ONE CAPSTONE PER TOWER');
    }
    const cost = Math.ceil(TIER_COSTS[tier]! * this.mods.costMult); // INFLATION
    if (this.state.resources.alloy < cost - 1e-6) return this.reject('NOT ENOUGH ALLOY');
    this.state.resources.alloy = Math.max(0, this.state.resources.alloy - cost);
    st.upgrades[path] = tier + 1;
    this.events.push({ type: 'upgraded', structureId: st.id, def: st.def, path, tier: tier + 1 });
    return null;
  }

  private doPull(shoalId: string): string | null {
    const shoal = this.state.rim.find((x) => x.id === shoalId);
    if (!shoal) return this.reject('NO SUCH SHOAL');
    if (shoal.state !== 'circling') return this.reject('ALREADY DEALT WITH');

    const s = this.state;
    const doubleReel = s.waves.some((w) => !w.cleared);
    // Wages at the whistle (doc 04): the shift roster locks NOW. Panic-builds
    // during the fight work the NEXT shift; the fallen collect nothing.
    if (this.powerDirty) {
      recomputeConnectivity(this);
      this.powerDirty = false;
    }
    const roster = shiftRoster(this);

    // Doc 14: every time a DIFFERENT shoal is pulled, the others lose a pip.
    // Bosses are exempt — their menace is the gimmick, not a +30% hp tax
    // stacked on a fight that is already the depth's exam (doc 13 §L).
    for (const other of s.rim) {
      if (other.id === shoal.id || other.state !== 'circling' || other.riled) continue;
      if (other.boss) continue;
      other.patience--;
      if (other.patience <= 0) {
        other.riled = true;
        this.events.push({ type: 'riled', name: other.name });
      }
    }

    const { edge, along } = this.entryPoint(shoal.orbit);
    s.pullsMade++;
    const wave = {
      id: s.nextId++,
      shoalId: shoal.id,
      startTick: s.tick,
      edge,
      pullIndex: s.pullsMade,
      doubleReel,
      roster,
      riled: shoal.riled,
      cleared: false,
      // doc 08: pulling a boss past shoals you never fought pays a greed bonus
      skipsAtPull: s.rim.filter((x) => x.id !== shoal.id && x.state === 'circling').length,
    };
    s.waves.push(wave);
    s.lastPullTick = s.tick;
    shoal.state = 'active';

    for (const g of shoal.composition) {
      for (let k = 0; k < g.count; k++) {
        s.pendingSpawns.push({
          tick: s.tick + Math.round((g.timeOffset + k * GROUP_SPACING_S) * TICK_RATE),
          species: g.species,
          waveId: wave.id,
          edge,
          along,
        });
      }
    }
    this.events.push({ type: 'pulled', name: shoal.name, doubleReel });
    return null;
  }

  /**
   * Cast an ability (doc 07). All four cost stored resources — the economy IS
   * the mana bar — and every one of them is refused the moment the meta says
   * the player has not bought it.
   */
  private doCast(ability: AbilityId, x: number, y: number): string | null {
    const s = this.state;
    const def = abilitiesJson.abilities[ability];
    if (this.mods.allowedAbilities && !this.mods.allowedAbilities.includes(ability)) {
      return this.reject('LOCKED — BUY IT IN COLONY COMMAND');
    }
    if (s.tick < s.abilityReady[ability]) return this.reject(`${def.name} IS RELOADING`);
    if (x < 0 || y < 0 || x >= this.grid.size || y >= this.grid.size) {
      return this.reject('AIM AT THE MAP');
    }
    const cost = def.cost as { ammo?: number; alloy?: number; capacitor?: number };
    if (cost.ammo !== undefined && s.resources.ammo < cost.ammo - 1e-6) return this.reject('NOT ENOUGH AMMO');
    if (cost.alloy !== undefined && s.resources.alloy < cost.alloy - 1e-6) return this.reject('NOT ENOUGH ALLOY');
    if (cost.capacitor !== undefined && s.capacitor < cost.capacitor - 1e-6) {
      return this.reject('THE CAPACITOR IS FLAT');
    }
    if (cost.ammo !== undefined) s.resources.ammo = Math.max(0, s.resources.ammo - cost.ammo);
    if (cost.alloy !== undefined) s.resources.alloy = Math.max(0, s.resources.alloy - cost.alloy);
    if (cost.capacitor !== undefined) s.capacitor = Math.max(0, s.capacitor - cost.capacitor);
    s.abilityReady[ability] = s.tick + def.cooldown * TICK_RATE;

    castAbility(this, ability, x, y);

    this.events.push({ type: 'cast', ability: def.name });
    return null;
  }

  /**
   * Doc 14: entry = the authored spawn edge nearest the shoal's orbit position;
   * WHEN you pull decides WHERE it enters. Orbit parametrises the perimeter
   * n → e → s → w; `along` is where on the chosen edge the shoal peels off.
   */
  entryPoint(orbit: number): { edge: EdgeId; along: number } {
    const edges = this.scenario.map.spawnEdges;
    let best: EdgeId = edges[0]!;
    let bestDist = Infinity;
    for (const e of edges) {
      const centre = (EDGE_ORDER.indexOf(e) + 0.5) / 4;
      const d = Math.abs(orbit - centre);
      const wrap = Math.min(d, 1 - d);
      if (wrap < bestDist) {
        bestDist = wrap;
        best = e;
      }
    }
    const side = EDGE_ORDER.indexOf(best);
    const p = orbit * 4 - side; // position within the chosen side's span
    const along = p < 0 ? 0.1 : p > 1 ? 0.9 : p;
    return { edge: best, along };
  }

  // -- systems --------------------------------------------------------------

  private recomputeFlow(): FlowField {
    this.flowDirty = false;
    return computeFlowField(this.grid, this.state.structures, this.byId(this.state.coreId)!);
  }

  private runEconomySystems(): void {
    if (this.powerDirty) {
      recomputeConnectivity(this);
      this.powerDirty = false;
    }
    refreshEconomyStatus(this);
  }

  /** Next payday preview (wages at the whistle): same maths, no mutation. */
  forecast(): Resources {
    if (this.powerDirty) {
      recomputeConnectivity(this);
      this.powerDirty = false;
    }
    return settleShift(this, shiftRoster(this), false);
  }

  /**
   * RUSH HOUR (doc 08 Pressure). The one modifier that touches the LURE itself:
   * the rim stops waiting, and pulls the next circling shoal `autoLureSeconds`
   * after the last pull started. Everything else about the run is unchanged —
   * the player still chooses WHICH, they just no longer choose when.
   */
  private autoLure(): void {
    const every = this.mods.autoLureSeconds;
    if (every <= 0) return;
    const s = this.state;
    if (s.lastPullTick < 0) return; // it never grabs the FIRST one for you
    if (s.tick - s.lastPullTick < every * TICK_RATE) return;
    // Act 1's own gate still holds: the boss is the player's to time (doc 14)
    const next = s.rim.find((x) => x.state === 'circling' && !x.boss);
    if (!next) return;
    this.doPull(next.id);
    this.events.push({ type: 'autoLure', name: next.name });
  }

  private spawnPending(): void {
    const s = this.state;
    if (s.pendingSpawns.length === 0) return;
    const due = s.pendingSpawns.filter((p) => p.tick <= s.tick);
    if (due.length === 0) return;
    s.pendingSpawns = s.pendingSpawns.filter((p) => p.tick > s.tick);
    for (const p of due) {
      const wave = s.waves.find((w) => w.id === p.waveId);
      const sp = SPECIES[p.species]!;
      const at = p.atX !== undefined ? { i: p.atX, j: p.atY! } : this.spawnTile(p.edge, p.along);
      // doc 14 Riled, then doc 08's Pressure: everyone thicker, bosses thicker still
      let hpMult = wave?.riled ? RILED_HP_MULT : 1;
      hpMult *= this.mods.enemyHpMult;
      if (sp.boss !== null) hpMult *= this.mods.bossHpMult;
      // two clocks, because the Sovereign runs two: the first carries grabs,
      // dives, threads and pulses, the second carries escort waves
      const every =
        sp.grab?.everySeconds ?? sp.dive?.everySeconds ?? sp.threads?.everySeconds ?? sp.pulse?.everySeconds;
      const rate = this.mods.gimmickRateMult; // ANGRY LANDLORD: clocks run faster
      const gimmick = every === undefined ? -1 : s.tick + Math.round((every / rate) * TICK_RATE);
      const gimmick2 = sp.escort ? s.tick + Math.round((sp.escort.everySeconds / rate) * TICK_RATE) : -1;
      s.enemies.push({
        id: s.nextId++,
        species: p.species,
        x: at.i,
        y: at.j,
        hp: Math.round(sp.hp * hpMult),
        maxHp: Math.round(sp.hp * hpMult),
        speed: sp.speed,
        dps: sp.dps,
        armor: sp.armor,
        waveId: p.waveId,
        attackTargetId: -1,
        laneX: this.rng.range(-0.25, 0.25),
        laneY: this.rng.range(-0.25, 0.25),
        lastHitBy: '',
        slowPct: 0,
        slowUntil: -1,
        stunUntil: -1,
        bleedDps: 0,
        bleedUntil: -1,
        bleedSlowPct: 0,
        bleedSpreadRadius: 0,
        bleedSrc: '',
        burnDps: 0,
        burnUntil: -1,
        burnSrc: '',
        revealedUntil: -1,
        litUntil: -1,
        litAmp: 0,
        aoeUntil: -1,
        aoeAmp: 0,
        armorShredPct: 0,
        armorShredUntil: -1,
        regenPauseUntil: -1,
        gimmickTick: gimmick,
        gimmickTick2: gimmick2,
        divingUntil: -1,
        biteTick: -1,
        goalStructId: -1,
      });
    }
  }

  private spawnTile(edge: EdgeId, along: number): { i: number; j: number } {
    const n = this.grid.size;
    const a = Math.round(along * (n - 1));
    const at = (k: number): { i: number; j: number } => {
      const c = Math.max(0, Math.min(n - 1, k));
      if (edge === 'n') return { i: c, j: 0 };
      if (edge === 's') return { i: c, j: n - 1 };
      if (edge === 'w') return { i: 0, j: c };
      return { i: n - 1, j: c };
    };
    for (let d = 0; d < n; d++) {
      for (const k of d === 0 ? [a] : [a + d, a - d]) {
        const t = at(k);
        if (this.grid.terrain[this.grid.idx(t.i, t.j)] !== T_ROCK) return t;
      }
    }
    return at(a); // unreachable on any sane map
  }

  /** Ruin a structure (doc 03). Public because fauna is what does the eating. */
  destroyStructure(target: Structure): void {
    if (target.ruin || this.state.outcome !== 'playing') return; // already fell this tick
    target.hp = 0;
    if (target.kind === 'core') {
      this.state.outcome = 'lost'; // doc 02: Core at 0 → run ends immediately
      this.events.push({ type: 'defeat' });
      return;
    }
    target.ruin = true;
    target.ruinWavesLeft = RUIN_WAVES;
    target.targetId = -1;
    this.flowDirty = true;
    this.powerDirty = true;
    this.events.push({
      type: 'structureDestroyed',
      structureId: target.id,
      def: target.def,
      i: target.i,
      j: target.j,
    });
  }

  /** Doc 07 E: the Nano Repair Swarm welds while its six seconds last. */
  private mendStructures(): void {
    const s = this.state;
    for (const st of s.structures) {
      if (st.healUntil <= s.tick || st.ruin) continue;
      st.hp = Math.min(st.maxHp, st.hp + st.healRate * DT);
      if (st.healUntil <= s.tick + 1) st.healRate = 0;
    }
  }

  private reapEnemies(): void {
    const dead = this.state.enemies.filter((e) => e.hp <= 0);
    if (dead.length === 0) return;
    for (const e of dead) {
      this.events.push({ type: 'enemyDied', species: e.species, x: e.x, y: e.y });
      // doc 06: a piñata. Do not celebrate. The brood joins its mother's wave,
      // so the fight is not "cleared" until the children are dealt with too.
      // doc 06: her nodes are hers. When she goes, they go — otherwise a fight
      // can stall forever on six stationary 30 HP dots nothing has range on.
      const threads = SPECIES[e.species]!.threads;
      if (threads) {
        for (const other of this.state.enemies) {
          if (other.species === threads.species && other.waveId === e.waveId) other.hp = 0;
        }
        this.state.pendingSpawns = this.state.pendingSpawns.filter(
          (sp) => !(sp.species === threads.species && sp.waveId === e.waveId),
        );
      }
      const brood = SPECIES[e.species]!.onDeathSpawn;
      if (brood) {
        for (let k = 0; k < brood.count; k++) {
          this.state.pendingSpawns.push({
            tick: this.state.tick + 1 + k * 2,
            species: brood.species,
            waveId: e.waveId,
            edge: 'e',
            along: 0.5,
            atX: e.x,
            atY: e.y,
          });
        }
      }
      const st = this.state.stats;
      if (e.lastHitBy !== '') st.waveKillsByDef[e.lastHitBy] = (st.waveKillsByDef[e.lastHitBy] ?? 0) + 1;
      if (!st.killedSpecies.includes(e.species)) st.killedSpecies.push(e.species);
      // Tetanus Express (doc 05 Stabby C3): the bleed jumps ship on death
      if (e.bleedSpreadRadius > 0 && e.bleedUntil > this.state.tick) {
        for (const other of this.state.enemies) {
          if (other.hp <= 0 || other.id === e.id) continue;
          const dx = other.x - e.x;
          const dy = other.y - e.y;
          if (dx * dx + dy * dy > e.bleedSpreadRadius * e.bleedSpreadRadius) continue;
          other.bleedDps = Math.max(other.bleedDps, e.bleedDps);
          other.bleedUntil = Math.max(other.bleedUntil, e.bleedUntil);
          other.bleedSlowPct = Math.max(other.bleedSlowPct, e.bleedSlowPct);
        }
      }
    }
    this.state.enemies = this.state.enemies.filter((e) => e.hp > 0);
  }

  private checkWaveClears(): void {
    const s = this.state;
    for (const w of s.waves) {
      if (w.cleared) continue;
      const pending = s.pendingSpawns.some((p) => p.waveId === w.id);
      const alive = s.enemies.some((e) => e.waveId === w.id);
      if (pending || alive) continue;

      w.cleared = true;
      s.fightsCleared++;
      const shoal = s.rim.find((x) => x.id === w.shoalId)!;
      shoal.state = 'cleared';

      // Doc 04 salvage: (20 + 8n)·d ore-equivalents as 60/30/10 ore/alloy/ammo;
      // doc 14 tempo bonuses multiply the payout.
      // payday: the shift's wages first (roster survivors), then the salvage
      const wages = settleShift(this, w.roster, true);
      const bonus = w.doubleReel ? this.mods.doubleReelBonus : 0;
      const p = (SALVAGE_BASE + SALVAGE_PER_N * s.fightsCleared) * this.scenario.depth * (1 + bonus);
      const salvage: Resources = {
        ore: Math.round(p * 0.6),
        alloy: Math.round((p * 0.3) / 2), // 2 ore-equiv per alloy (doc 04 chains)
        ammo: Math.round(p * 0.1),
        gas: 0,
        fuel: 0,
      };
      for (const r of RES_IDS) this.addResource(r, salvage[r]);
      // the meta's half of the payday (doc 08): this wave's XP inputs ride the
      // event, and the accumulators reset here so nothing is ever paid twice
      const cleared: Extract<SimEvent, { type: 'waveCleared' }> = {
        type: 'waveCleared',
        shoalId: shoal.id,
        name: shoal.name,
        salvage,
        wages,
        bonus: w.doubleReel ? 'double' : null,
        riled: w.riled,
        skips: w.skipsAtPull,
        xpDamage: { ...s.stats.waveDamageByDef },
        xpKillers: Object.keys(s.stats.waveKillsByDef),
        killedSpecies: [...s.stats.killedSpecies],
      };
      if (shoal.boss) cleared.boss = shoal.boss;
      s.stats.waveDamageByDef = {};
      s.stats.waveKillsByDef = {};
      this.events.push(cleared);

      // ruins release their footprint one wave after they fell (doc 03)
      let ruinsFreed = false;
      for (const x of s.structures) {
        if (x.ruin) x.ruinWavesLeft--;
      }
      if (s.structures.some((x) => x.ruin && x.ruinWavesLeft < 0)) {
        s.structures = s.structures.filter((x) => !(x.ruin && x.ruinWavesLeft < 0));
        ruinsFreed = true;
      }
      if (ruinsFreed) {
        this.grid.sync(s.structures);
        this.flowDirty = true;
        this.powerDirty = true;
      }

      // Act gate (docs 02/14): the mini-boss's death unlocks Act 2 — its
      // shoals and the boss join the rim; uncleared Act 1 shoals keep circling.
      if (shoal.boss === 'miniboss' && s.act === 1) {
        s.act = 2;
        const n = this.scenario.act2Shoals.length;
        this.scenario.act2Shoals.forEach((def, k) => {
          s.rim.push(this.makeRimShoal(def, (shoal.orbit + (k + 1) / (n + 1)) % 1));
        });
        this.events.push({ type: 'act2' });
      }
      // The real win rule (doc 02): the boss dies → depth cleared, instantly.
      // Remaining shoals swim away; their rewards are forfeited.
      if (shoal.boss === 'boss') {
        s.outcome = 'won';
        this.events.push({ type: 'victory' });
      }
    }
  }

  private addResource(res: keyof Resources, amount: number): void {
    // Doc 04: storage caps are the turtle guard (100 + 100 per Depot). Losing
    // a Depot never destroys stock — the cap only gates NEW income.
    const cap = Math.max(this.storageCap, this.state.resources[res]);
    this.state.resources[res] = Math.max(0, Math.min(cap, this.state.resources[res] + amount));
  }
}
